import type { Router } from 'vue-router'
import { ref } from 'vue'
import { isDesktopRuntime } from '@/platform/bridge'

const AUTO_RELOAD_COOLDOWN_MS = 60 * 1000
const VERSION_CHECK_INTERVAL_MS = 10 * 1000
const VERSION_CHECK_THROTTLE_MS = VERSION_CHECK_INTERVAL_MS
const LAST_RELOAD_REASON_KEY = 'aihelper:web-version:last-reload-reason'
const LAST_RELOAD_AT_KEY = 'aihelper:web-version:last-reload-at'
const CHUNK_LOAD_ERROR_MARKERS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
  'loading chunk',
  'chunkloaderror',
]

let recovering = false
let started = false
let versionCheckTimer: number | null = null
let lastVersionCheckAt = 0

export const webVersionUpdateAvailable = ref(false)

function versionCheckEnabled(): boolean {
  return !isDesktopRuntime
}

function readBuildVersion(): string {
  return window.__APP_VERSION__?.trim() || ''
}

/** 构建带时间戳的版本清单地址，强制绕过 CDN 与浏览器缓存。 */
function buildVersionManifestURL(): string {
  const url = new URL(`${import.meta.env.BASE_URL}version.json`, window.location.href)
  url.searchParams.set('t', String(Date.now()))
  return url.toString()
}

/** 仅清理浏览器页面缓存，避免刷新后继续命中 CDN 或 Service Worker 的旧资源。 */
async function clearBrowserPageCaches(): Promise<void> {
  const cleanupTasks: Promise<unknown>[] = []

  if (typeof caches !== 'undefined') {
    cleanupTasks.push(
      caches
        .keys()
        .then((cacheNames) => Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))),
    )
  }

  if ('serviceWorker' in navigator) {
    cleanupTasks.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((registration) => registration.unregister())),
        ),
    )
  }

  await Promise.allSettled(cleanupTasks)
}

/** 清理页面缓存后重新加载，恢复发布时命中旧 chunk 或 CSS 的已打开页面。 */
export function forceReloadWithCacheBust(reason = 'version-change') {
  // 桌面客户端资源随安装包内置，网页缓存刷新只属于浏览器运行时。
  if (isDesktopRuntime || recovering) return
  recovering = true

  try {
    const now = Date.now()
    const lastReason = sessionStorage.getItem(LAST_RELOAD_REASON_KEY)
    const lastReloadAt = Number(sessionStorage.getItem(LAST_RELOAD_AT_KEY) || 0)
    if (
      reason !== 'version-change' &&
      lastReason === reason &&
      now - lastReloadAt < AUTO_RELOAD_COOLDOWN_MS
    ) {
      recovering = false
      return
    }

    sessionStorage.setItem(LAST_RELOAD_REASON_KEY, reason)
    sessionStorage.setItem(LAST_RELOAD_AT_KEY, String(now))
  } catch {
    // 隐私模式等场景下 sessionStorage 不可用，仍继续刷新一次恢复页面。
  }

  void clearBrowserPageCaches().finally(() => {
    window.location.reload()
  })
}

/** 浏览器专属的网页版本刷新入口，桌面客户端继续走软件更新流程。 */
export function refreshWebVersion() {
  if (isDesktopRuntime) return
  forceReloadWithCacheBust('version-change')
}

export function isChunkLoadError(error: unknown): boolean {
  let message = ''
  try {
    if (error instanceof Error) {
      message = error.message
    } else if (typeof error === 'object' && error !== null && 'message' in error) {
      message = String((error as { message?: unknown }).message || '')
    } else {
      message = String(error || '')
    }
  } catch {
    return false
  }

  const normalized = message.toLowerCase()
  return CHUNK_LOAD_ERROR_MARKERS.some((marker) => normalized.includes(marker))
}

export function recoverFromChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false
  forceReloadWithCacheBust('chunk-load-error')
  return true
}

function listenForChunkLoadErrors(router?: Router) {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    forceReloadWithCacheBust('vite-preload-error')
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (recoverFromChunkLoadError(event.reason)) {
      event.preventDefault()
    }
  })

  router?.onError((error) => {
    recoverFromChunkLoadError(error)
  })
}

/** 以最近一次真实请求为起点，继续保持 10 秒静默轮询。 */
function scheduleVersionPolling(router?: Router) {
  if (versionCheckTimer) window.clearTimeout(versionCheckTimer)
  versionCheckTimer = null

  versionCheckTimer = window.setTimeout(() => {
    requestVersionCheck(router)
  }, VERSION_CHECK_INTERVAL_MS)
}

function isLoginRoute(route?: { path: string }): boolean {
  return route?.path === '/login'
}

/** 统一版本检测入口：登录页不请求，并合并短时间内的重定向触发。 */
function requestVersionCheck(router?: Router) {
  if (isLoginRoute(router?.currentRoute.value)) return
  if (!readBuildVersion()) return

  const now = Date.now()
  if (now - lastVersionCheckAt < VERSION_CHECK_THROTTLE_MS) return
  lastVersionCheckAt = now

  void checkVersionManifest().catch(() => {
    // 网络瞬断或版本文件短暂不可用时不打扰用户，下一轮继续检查。
  })
  scheduleVersionPolling(router)
}

/** 读取远端版本清单，并与当前构建内置版本比较。 */
async function checkVersionManifest() {
  const buildVersion = readBuildVersion()
  if (!buildVersion) return

  const response = await fetch(buildVersionManifestURL(), {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })
  if (!response.ok) return

  const manifest = (await response.json()) as { version?: unknown }
  if (typeof manifest.version !== 'string' || !manifest.version.trim()) return
  const remoteVersion = manifest.version.trim()
  if (remoteVersion === buildVersion) {
    webVersionUpdateAvailable.value = false
    return
  }

  webVersionUpdateAvailable.value = true
}

/**
 * 浏览器页面的静态资源更新自愈：chunk/CSS 失效时自动刷新恢复。
 * 桌面端使用随安装包内置的资源，因此不挂静态资源错误监听。
 */
export function startVersionCheck(router?: Router) {
  if (!versionCheckEnabled() || started) return
  started = true

  listenForChunkLoadErrors(router)
  if (!readBuildVersion()) return

  const check = () => {
    requestVersionCheck(router)
  }

  router?.afterEach((to) => {
    if (isLoginRoute(to)) return
    check()
  })
  void router?.isReady().then(check)
}
